import Button, { ButtonTypes } from "atoms/buttons/button";
import Select, { SelectOption } from "atoms/forms/select";
import Loader from "atoms/loaders/loader";
import SectionRecord from "models/view-models/section-record";
import PublicationRecord from "models/view-models/publication-record";
import Typeahead from "molecules/typeahead/typeahead";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import usePublications from "utilities/hooks/domain/publications/use-publications";
import { ToastManager } from "utilities/toast/toast-manager";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { CodeReferencePill } from "organisms/code-reference-pill/code-reference-pill";
import { t } from "utilities/localization-utils";
import SectionService from "utilities/services/sections/section-service";
import PublicationContentFormatType from "utilities/enumerations/publication-content-format-type";
import usePageLimits from "pages/notebooks/use-page-limits";
import Tooltip from "molecules/tooltips/tooltip";

const BASE_CLASS_NAME = "c-checklist-item-note-sections-select";

interface ChecklistItemNoteSectionsSelectProps {
    onSelected: (newValue: SectionRecord) => void;
    onRemoved: (removedValue: SectionRecord) => void;
    sections: Array<SectionRecord>;
    onSelectIsActive: (isActive: boolean) => void;
}

const ChecklistItemNoteSectionsSelect: React.FC<
    ChecklistItemNoteSectionsSelectProps
> = ({ onSelected, onRemoved, sections, onSelectIsActive }) => {
    const { list: searchSectionsApi } = SectionService.useList();
    const { pageLimits } = usePageLimits();
    const [selectedSection, setSelectedSection] = useState<
        SectionRecord | undefined
    >();

    const [publicationId, setPublicationId] = useState(0);
    const { resultObject: publications, loading: loadingPublications } =
        usePublications({});
    const codeReferenceLimitReached =
        pageLimits?.checklistItemNoteCodeReferenceLimit &&
        ((pageLimits?.checklistItemNoteCodeReferenceLimit <=
            sections.length) as boolean | undefined);
    const publicationOptions = useMemo(() => {
        const publicationList = CollectionUtils.isEmpty(publications)
            ? []
            : publications;
        const options = publicationList
            .filter(
                (p) =>
                    p.publicationContentFormatType !==
                    PublicationContentFormatType.Image
            )
            .map(
                (p: PublicationRecord): SelectOption<undefined, number> => ({
                    label: p.getDisplayCodeAndEdition(),
                    value: p.id!,
                })
            );
        options.unshift({
            label: t("findAPublication"),
            value: 0,
            isPlaceholder: true,
        });
        return options;
    }, [publications]);

    const searchSections = useCallback(
        async (searchText: string) => {
            try {
                const result = await searchSectionsApi({
                    searchText: searchText,
                    publicationId:
                        publicationId === 0 ? undefined : publicationId,
                    take: 20,
                });
                return result.resultObjects;
            } catch (e) {
                ToastManager.error("There was an issue searching sections.");
                return [];
            }
        },
        [searchSectionsApi, publicationId]
    );

    const getOptionText = (section: SectionRecord) =>
        section.getFullyQualifiedDisplayTitle();

    const handlePublicationSelected = (
        selectedOption?: SelectOption<undefined, number>
    ) => {
        setSelectedSection(undefined);

        if (selectedOption == null) {
            setPublicationId(0);
            return;
        }

        setPublicationId(selectedOption.value);
    };

    const handleApply = () => {
        if (selectedSection == null) {
            return;
        }
        if (
            pageLimits?.checklistItemNoteCodeReferenceLimit &&
            pageLimits?.checklistItemNoteCodeReferenceLimit <= sections.length
        ) {
            ToastManager.warn(
                t("exceedsCodeReferenceLimit", {
                    limit: pageLimits?.checklistItemNoteCodeReferenceLimit!,
                })
            );
            return [];
        }
        let sectionWithPublication = selectedSection;
        if (selectedSection.publicationId != null) {
            const publication = publications?.find(
                (p) => p.id === selectedSection.publicationId
            );
            if (publication != null) {
                sectionWithPublication = selectedSection.with({
                    publication: publication,
                });
            }
        }

        onSelected(sectionWithPublication);
        setSelectedSection(undefined);
    };

    const handleReferencePillClose = (id: number) => () => {
        const record = sections.find(
            (section: SectionRecord) => section.id === id
        );
        if (record != null) {
            onRemoved(record);
        }
    };

    // This useEffect bubbles up whether the user has a section currently selected so that the modal can disable the save button when the section hasn't been added to the checklist item note yet
    useEffect(() => {
        onSelectIsActive(selectedSection !== undefined);
    }, [selectedSection, onSelectIsActive]);

    if (loadingPublications) {
        return <Loader accessibleText={"Loading publications..."} />;
    }

    return (
        <div className={BASE_CLASS_NAME}>
            <div className={`${BASE_CLASS_NAME}__select`}>
                <Select<undefined, number>
                    id="publication-select"
                    onChange={handlePublicationSelected}
                    options={publicationOptions}
                    value={publicationId}
                />
                <Typeahead<SectionRecord>
                    disabled={!publicationId}
                    cssClassName={`${BASE_CLASS_NAME}__sections-typeahead`}
                    label={""}
                    getOptionText={getOptionText}
                    onChange={setSelectedSection}
                    search={searchSections}
                    value={selectedSection}
                    minSearchCharacters={1}
                    debounceDelayInMs={400}
                />
                <Tooltip
                    content={t(
                        "exceedsCodeReferenceLimitPerChecklistItemNote",
                        {
                            limit: pageLimits?.checklistItemNoteCodeReferenceLimit!,
                        }
                    )}
                    cssClassName={`${BASE_CLASS_NAME}__tooltip`}
                    disabled={!codeReferenceLimitReached}
                    durationInMs={0}
                    hideOnClick={true}>
                    <span className={`${BASE_CLASS_NAME}__tooltip-wrapper`}>
                        <Button
                            disabled={
                                selectedSection === undefined ||
                                !!codeReferenceLimitReached
                            }
                            onClick={handleApply}
                            type={ButtonTypes.Button}
                            cssClassName={`${BASE_CLASS_NAME}__add-button `}>
                            <Icon type={Icons.Plus} />
                            <span
                                className={`${BASE_CLASS_NAME}__add-button-text`}>
                                {t("add")}
                            </span>
                        </Button>
                    </span>
                </Tooltip>
            </div>
            {CollectionUtils.hasValues(sections) && (
                <div className={`${BASE_CLASS_NAME}__grouping`}>
                    {sections.map((section: SectionRecord, index: number) => {
                        const publication = publications?.find(
                            (p: PublicationRecord) =>
                                p.id === section.publicationId
                        );
                        return (
                            <CodeReferencePill
                                key={section.id ?? index}
                                section={section}
                                publication={publication}
                                removeable={true}
                                onReferencePillClose={handleReferencePillClose(
                                    section.id!
                                )}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ChecklistItemNoteSectionsSelect;
